import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
  Activity, Database, ArrowUpRight, ArrowDownRight, 
  Play, Pause, Settings, Bell, BellOff, Zap, Server,
  Upload, Download, TrendingUp, Shield, Eye, History
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KPISyncMonitor() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState([]);
  const [queueStatus, setQueueStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, historyRes, alertsRes, trendsRes, queueRes] = await Promise.all([
        fetch(`${API_URL}/api/caresync/monitor/status`, { headers }),
        fetch(`${API_URL}/api/caresync/monitor/history?limit=20`, { headers }),
        fetch(`${API_URL}/api/caresync/monitor/alerts?acknowledged=false&limit=10`, { headers }),
        fetch(`${API_URL}/api/caresync/monitor/trends?days=7`, { headers }),
        fetch(`${API_URL}/api/caresync/queue/status`, { headers })
      ]);

      if (statusRes.ok) setSyncStatus(await statusRes.json());
      if (historyRes.ok) {
        const data = await historyRes.json();
        setSyncHistory(data.history || []);
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends || []);
      }
      if (queueRes.ok) setQueueStatus(await queueRes.json());
    } catch (error) {
      console.error('Failed to fetch sync data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleRetryFailed = async () => {
    try {
      const res = await fetch(`${API_URL}/api/caresync/retry/failed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Retry initiated for failed syncs');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to retry syncs');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/caresync/monitor/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleTestWebhook = async () => {
    try {
      const res = await fetch(`${API_URL}/api/caresync/test/webhook`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.test_status === 'passed') {
        toast.success('Webhook test passed!');
      } else {
        toast.error('Webhook test failed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to run webhook test');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-emerald-600 bg-emerald-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-amber-600 bg-amber-100';
      case 'partial': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'bg-emerald-500';
      case 'warning': return 'bg-amber-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kpi-sync-monitor">
      {/* Modern Header */}
      <PageHeader
        icon={Activity}
        title="KPI Sync Monitor"
        subtitle="CareSync Engine - Real-time data synchronization dashboard"
        gradient="from-teal-600 via-cyan-600 to-emerald-600"
      >
        <div className="flex gap-2">
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" size="sm" onClick={handleTestWebhook}>
            <Zap className="h-4 w-4 mr-2" />
            Test Webhook
          </Button>
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Sync Health"
          value={syncStatus?.health || 'Unknown'}
          subtitle="System Status"
          icon={Shield}
          gradient={syncStatus?.health === 'healthy' ? 'emerald' : 'amber'}
        />
        <MetricCard
          title="Last Sync"
          value={syncStatus?.last_sync_time ? new Date(syncStatus.last_sync_time).toLocaleTimeString() : 'Never'}
          subtitle={syncStatus?.last_sync_status || 'N/A'}
          icon={Clock}
          gradient="blue"
        />
        <MetricCard
          title="Records Today"
          value={syncStatus?.records_synced_today || 0}
          subtitle="Synced Records"
          icon={Database}
          gradient="violet"
        />
        <MetricCard
          title="Active Alerts"
          value={alerts.length}
          subtitle={alerts.length > 0 ? 'Attention Needed' : 'All Clear'}
          icon={alerts.length > 0 ? Bell : BellOff}
          gradient={alerts.length > 0 ? 'rose' : 'emerald'}
        />
      </div>

      {/* 24h Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <ModernCard className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-600">{syncStatus?.syncs_24h?.success || 0}</p>
            <p className="text-xs text-muted-foreground">Successful (24h)</p>
          </CardContent>
        </ModernCard>
        
        <ModernCard className="bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-900 border-red-200 dark:border-red-800">
          <CardContent className="p-4 text-center">
            <XCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-600">{syncStatus?.syncs_24h?.failed || 0}</p>
            <p className="text-xs text-muted-foreground">Failed (24h)</p>
          </CardContent>
        </ModernCard>
        
        <ModernCard className="bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-900 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-600">{syncStatus?.syncs_24h?.pending || 0}</p>
            <p className="text-xs text-muted-foreground">Pending (24h)</p>
          </CardContent>
        </ModernCard>
        
        <ModernCard className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-900 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4 text-center">
            <Activity className="h-6 w-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{syncStatus?.syncs_24h?.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total (24h)</p>
          </CardContent>
        </ModernCard>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="history">Sync History</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="queue">Queue Status</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Sync Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  7-Day Sync Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="success" stackId="1" stroke="#10B981" fill="#10B98133" name="Success" />
                    <Area type="monotone" dataKey="failed" stackId="1" stroke="#EF4444" fill="#EF444433" name="Failed" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Records Processed Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Records Processed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="records_processed" fill="#6366F1" name="Records" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Recent Alerts Preview */}
          {alerts.length > 0 && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-900/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Active Alerts ({alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {alerts.slice(0, 3).map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <div>
                        <Badge className={getSeverityBadge(alert.severity)}>{alert.severity}</Badge>
                        <p className="text-sm mt-1">{alert.message}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAcknowledgeAlert(alert.sync_id)}
                      >
                        Acknowledge
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Sync History
              </CardTitle>
              {syncHistory.some(s => s.status === 'failed') && (
                <Button size="sm" variant="outline" onClick={handleRetryFailed}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Failed
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sync ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Errors</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncHistory.map((sync, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-xs">{sync.sync_id?.slice(0, 20)}...</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(sync.status)}>{sync.status}</Badge>
                      </TableCell>
                      <TableCell>{sync.source_type}</TableCell>
                      <TableCell>{sync.records_processed}</TableCell>
                      <TableCell>{sync.error_count || 0}</TableCell>
                      <TableCell className="text-xs">
                        {new Date(sync.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {syncHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No sync history yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                All Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <BellOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No active alerts</p>
                  <p className="text-sm">All systems operating normally</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                      alert.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getSeverityBadge(alert.severity)}>{alert.severity}</Badge>
                            <Badge variant="outline">{alert.type}</Badge>
                          </div>
                          <p className="font-medium">{alert.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                          {alert.notify && (
                            <p className="text-xs mt-2">
                              <span className="text-muted-foreground">Notify:</span>{' '}
                              {alert.notify.join(', ')}
                            </p>
                          )}
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => handleAcknowledgeAlert(alert.sync_id)}
                        >
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Queue Tab */}
        <TabsContent value="queue">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Queue Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    <span>Pending</span>
                  </div>
                  <span className="text-xl font-bold">{queueStatus?.pending || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    <span>Processing</span>
                  </div>
                  <span className="text-xl font-bold">{queueStatus?.processing || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span>Failed (Awaiting Retry)</span>
                  </div>
                  <span className="text-xl font-bold">{queueStatus?.failed_awaiting_retry || 0}</span>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span>Queue Health</span>
                    <Badge className={`${
                      queueStatus?.queue_health === 'healthy' ? 'bg-emerald-100 text-emerald-700' :
                      queueStatus?.queue_health === 'warning' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {queueStatus?.queue_health || 'Unknown'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Sync Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium">Real-Time Sync</p>
                  <p className="text-xs text-muted-foreground">Webhook-triggered instant sync</p>
                  <Badge className="mt-2 bg-emerald-100 text-emerald-700">Active</Badge>
                </div>
                
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium">Scheduled Sync</p>
                  <p className="text-xs text-muted-foreground">Every 15 minutes</p>
                  <Badge className="mt-2 bg-emerald-100 text-emerald-700">Active</Badge>
                </div>
                
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium">Daily Reconciliation</p>
                  <p className="text-xs text-muted-foreground">Full sync at 5:00 AM</p>
                  <Badge className="mt-2 bg-emerald-100 text-emerald-700">Active</Badge>
                </div>
                
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium">Retry Policy</p>
                  <p className="text-xs text-muted-foreground">3 retries, then escalate</p>
                  <Badge className="mt-2 bg-blue-100 text-blue-700">Configured</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
